import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Footer } from "@/components/Footer";

const HERO = "https://images.unsplash.com/photo-1610481470055-fe557fcf5683?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxjcmlja2V0JTIwaW5zZWN0JTIwbWFjcm98ZW58MHx8fHwxNzg1NTU0MDY4fDA&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay }}>
      {children}
    </motion.span>
  </span>
);

const CHAPTERS = [
  { n: "01", t: "Nutrition, not filler", d: "Every feeder is gut-loaded on a proprietary diet for a full 24 hours before it leaves our facility. What your animal eats, ate well first." },
  { n: "02", t: "Overnight, always", d: "Insulated, temperature-controlled packaging with live-arrival guarantee. From our colony to your enclosure in under 24 hours." },
  { n: "03", t: "Bred, never caught", d: "Captive-bred across five species. No wild collection, no parasites, no surprises. Traceable from egg to order." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    api.get("/products").then((r) => setFeatured(r.data.filter((p) => p.featured).slice(0, 3)));
  }, []);

  return (
    <div className="App bg-[#080808]">
      {/* HERO */}
      <section ref={heroRef} className="relative h-screen flex flex-col justify-center overflow-hidden">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
          <img src={HERO} alt="feeder cricket macro" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/40 to-[#080808]/70" />
        </motion.div>

        <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="overline mb-6">Live & Frozen Feeders — Est. 2026</motion.p>
          <h1 className="font-display font-light text-[15vw] md:text-[11vw] leading-[0.82] tracking-tighter text-[#F4F1EC]">
            <Line delay={0.1}>Feed the</Line>
            <Line delay={0.22}>instinct<span className="text-[#C7F000]">.</span></Line>
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }} className="flex flex-col md:flex-row md:items-end gap-6 mt-10">
            <p className="text-[#8A8782] text-sm max-w-md leading-relaxed">Crickets, dubia roaches, mealworms, rats and mice — raised with obsessive standards for the animals that depend on them.</p>
            <Link to="/shop" data-testid="hero-shop-btn" className="btn-toxic px-8 py-4 inline-flex items-center gap-3 w-fit">Shop Feeders <ArrowRight size={16} /></Link>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-8 border-y border-[#222] bg-[#0a0a0a]">
        <Marquee speed={40} gradient={false}>
          {["Acheta Domesticus", "Blaptica Dubia", "Rattus Norvegicus", "Tenebrio Molitor", "Mus Musculus"].map((x, i) => (
            <span key={i} className="font-display text-4xl md:text-5xl font-light text-[#F4F1EC] mx-10 flex items-center gap-10">
              {x} <span className="text-[#C7F000] text-2xl">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-28">
        <div className="grid md:grid-cols-12 gap-10 mb-20">
          <p className="overline md:col-span-3">The Standard</p>
          <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight md:col-span-9 leading-[1.05]">We treat feeder insects like livestock, because your animals treat them like life.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-[#222] border border-[#222]">
          {CHAPTERS.map((c, i) => (
            <motion.div key={c.n} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.1 }} className="bg-[#080808] p-10 md:p-12">
              <span className="font-display text-6xl text-[#C7F000]">{c.n}</span>
              <h3 className="font-display text-3xl mt-6 mb-4">{c.t}</h3>
              <p className="text-[#8A8782] text-sm leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10">
        <div className="flex items-end justify-between mb-12">
          <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight">Signature feeders</h2>
          <Link to="/shop" className="overline link-underline hidden md:inline-flex items-center gap-2">View all <ArrowRight size={14} /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {featured.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
        </div>
      </section>

      <Footer />
    </div>
  );
}
